if (!customElements.get('mbr-routine-builder')) {
  customElements.define(
    'mbr-routine-builder',
    class MbrRoutineBuilder extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        /* Nichts ist vorausgewählt — auch der Anker nicht. Die Leiste
           "Your protocol" bleibt verborgen, bis mindestens ein Add-Klick
           passiert ist (siehe updateUI). */
        this.selected = new Set();

        this.initProductStickyFormVisibility();
        this.initAnchorPosition();

        /* Die ganze Karte toggelt. Der Toggle-Button ist vom Karten-
           Handler ausgenommen, damit ein Klick nicht doppelt schaltet. */
        this.cards.forEach((card) => {
          this.on(card, 'click', (event) => {
            if (event.target.closest('[data-rb-toggle]')) return;
            /* Das Foto öffnet die Quick View und darf die Phase NICHT
               zusätzlich mit umschalten. <quick-view> selbst liegt bis
               zum ersten Öffnen ebenfalls noch in der Karte (danach
               verschiebt es das Theme an <body>) — solange müssen auch
               Klicks daraus hier durchfallen. */
            if (event.target.closest('[data-rb-quickview]')) return;
            if (event.target.closest('quick-view')) return;
            this.togglePhase(Number(card.getAttribute('data-phase')));
          });
        });

        /* Das Theme meldet nach dem Nachladen des Modal-Inhalts
           'quickview:loaded' auf document (theme.js:7835) — der einzige
           Zeitpunkt, zu dem der Modal-Inhalt im DOM steht. */
        this.on(document, 'quickview:loaded', this.onQuickViewLoaded.bind(this));

        this.toggles.forEach((toggle) => {
          this.on(toggle, 'click', () => {
            this.togglePhase(Number(toggle.closest('[data-rb-card]').getAttribute('data-phase')));
          });
        });

        this.pills.forEach((pill) => {
          this.on(pill, 'click', () => {
            this.togglePhase(Number(pill.getAttribute('data-phase')), 'pill');
          });
        });

        if (this.submitButton) this.on(this.submitButton, 'click', this.onSubmitHandler.bind(this));

        this.updateUI();
      }

      get anchorPhase() {
        return Number(this.getAttribute('data-anchor-phase'));
      }

      get cards() {
        return Array.from(this.querySelectorAll('[data-rb-card]'));
      }

      get toggles() {
        return Array.from(this.querySelectorAll('[data-rb-toggle]'));
      }

      get pills() {
        return Array.from(this.querySelectorAll('[data-rb-pill]'));
      }

      get submitButton() {
        return this.querySelector('[data-rb-submit]');
      }

      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      /* Die Product-Sticky-Form des Themes soll die Routine mobil nicht
         überdecken. Beobachtet wird der vollständige Shopify-Section-
         Wrapper einschließlich Headline und Padding. Die Animation selbst
         kommt aus dem CSS; JS verwaltet nur Zustand und Zugänglichkeit. */
      initProductStickyFormVisibility() {
        this.productStickyForm = document.querySelector('product-sticky-form');
        if (!this.productStickyForm || !('IntersectionObserver' in window)) return;

        this.productStickyForm.classList.add('mbr-rb-sticky-controlled');
        this.routineSectionInView = false;
        const routineSection = this.closest('.shopify-section') || this;
        const observer = new IntersectionObserver((entries) => {
          this.routineSectionInView = entries.some((entry) => entry.isIntersecting);
          this.updateProductStickyFormVisibility();
        });

        observer.observe(routineSection);
        this.on(document, 'matchSmall', this.updateProductStickyFormVisibility.bind(this));
        this.on(document, 'unmatchSmall', this.updateProductStickyFormVisibility.bind(this));
        this.registerCleanup(() => {
          observer.disconnect();
          this.setProductStickyFormHidden(false);
          this.productStickyForm.classList.remove('mbr-rb-sticky-controlled');
        });
      }

      /* Der Slider startet mobil auf der Karte des gerade besuchten
         Produkts statt immer bei Phase 1 — die eigene Phase trägt das
         "Currently viewing"-Label und gehört ins Bild.

         Abgewartet wird, bis der Container tatsächlich horizontal
         scrollbar ist. Grund: Die Section lädt ihr CSS lazy
         (media="print" onload), und bis es greift, stehen die Karten
         untereinander statt nebeneinander — alle mit identischem
         offsetLeft, die Positionsrechnung ergäbe dann 0. Der
         ResizeObserver feuert genau in dem Moment, in dem das Layout
         steht; danach meldet er sich ab, damit er dem Kunden später
         nicht in sein eigenes Wischen fährt. Die Section liegt weit
         unten auf der Produktseite, das passiert also lange bevor die
         Karten ins Blickfeld kommen. */
      initAnchorPosition() {
        const cards = this.querySelector('[data-rb-cards]');
        const observer = new ResizeObserver(() => {
          if (cards.scrollWidth <= cards.clientWidth) return;

          observer.disconnect();
          this.scrollToCard(this.anchorPhase, 'instant');
        });

        observer.observe(cards);
        this.registerCleanup(() => observer.disconnect());
      }

      updateProductStickyFormVisibility() {
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        this.setProductStickyFormHidden(isMobile && this.routineSectionInView);
      }

      setProductStickyFormHidden(hidden) {
        if (!this.productStickyForm) return;

        this.productStickyForm.classList.toggle('mbr-rb-sticky-hidden', hidden);
        this.productStickyForm.toggleAttribute('inert', hidden);
        if (hidden) {
          this.productStickyForm.setAttribute('aria-hidden', 'true');
        }
        else {
          this.productStickyForm.removeAttribute('aria-hidden');
        }
      }

      card(phase) {
        return this.querySelector('[data-rb-card][data-phase="' + phase + '"]');
      }

      phaseLabel(phase) {
        return this.getAttribute('data-phase-label-' + phase) || '';
      }

      isSelectable(phase) {
        const card = this.card(phase);
        return card && !card.hasAttribute('data-sold-out');
      }

      /* Mobil (Karten-Slider mit scroll-snap, <768px) nach dem Hinzufügen
         zur nächsten offenen Phase weiterscrollen, zyklisch über das
         Ende hinaus: Close führt zurück zu Open. Desktop-Grid bleibt
         unberührt, da dort kein horizontales Scrollen stattfindet. */
      advanceToNextCard(phase) {
        const next = this.nextOpenPhase(phase);
        if (next) this.scrollToCard(next);
      }

      /* Zyklisch ab der gerade gewählten Phase: die erste, die noch
         aussteht und kaufbar ist. Alles erledigt -> null, der Slider
         bleibt dann stehen. Die eigene Phase kommt nie in Frage, sie
         wurde soeben ausgewählt — daher nur zwei Schritte. */
      nextOpenPhase(phase) {
        for (let step = 1; step <= 2; step++) {
          const candidate = ((phase - 1 + step) % 3) + 1;
          if (!this.selected.has(candidate) && this.isSelectable(candidate)) return candidate;
        }

        return null;
      }

      /* Fährt eine Karte im mobilen Slider an. Desktop bleibt
         unberührt, dort stehen alle drei nebeneinander.

         Gescrollt wird ausschließlich der Karten-Container, nie das
         Dokument — scrollIntoView würde die Seite vertikal mitziehen
         und wäre damit für die Startposition unbrauchbar. Die Rechnung
         folgt dem Slider-Muster des Themes (theme.js:6519): absolute
         Zielposition aus der offsetLeft-Differenz zweier Geschwister-
         karten. Absolut, weil ein Delta aus getBoundingClientRect
         während einer laufenden Smooth-Animation eine Zwischenposition
         liest und sich Aufrufe aufaddieren würden; gegen Geschwister,
         weil .mbr-rb__cards nicht positioniert ist und der offsetParent
         weiter oben in der Section liegt. Der Zentrierungsterm bildet
         scroll-snap-align: center nach — an den Rändern klemmt der
         Browser den Wert selbst ab. */
      scrollToCard(phase, behavior = 'smooth') {
        if (!window.matchMedia('(max-width: 767px)').matches) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) behavior = 'instant';

        const container = this.querySelector('[data-rb-cards]');
        const card = this.card(phase);
        const first = this.card(1);

        container.scrollTo({
          left: card.offsetLeft - first.offsetLeft - (container.clientWidth - card.clientWidth) / 2,
          behavior
        });
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get quickViewAddLabel() {
        return this.getAttribute('data-quickview-add-label') || 'Add to routine';
      }

      /* Das geöffnete Modal gehört nicht mehr zu uns im DOM-Baum
         (ModalElement hängt es an <body>), also über Section-ID +
         Produkt-URL aus dem Event zuordnen. */
      onQuickViewLoaded(event) {
        const url = event.detail && event.detail.productUrl;
        if (!url) return;

        const modal = Array.from(
          document.querySelectorAll('quick-view[data-rb-quickview-modal][data-rb-section="' + this.sectionId + '"]')
        ).find((el) => el.getAttribute('data-product-url') === url);

        if (modal) this.injectRoutineButton(modal);
      }

      /* Antonios Entscheidung: der Routine-Button ERSETZT im Modal den
         normalen Kaufweg — zwei konkurrierende Kauf-Aktionen im selben
         Fenster würden die Routine-Summe unterlaufen. Ausverkaufte
         Phasen lassen wir unangetastet, dort zeigt das Theme bereits
         seinen deaktivierten "Sold out"-Button. */
      injectRoutineButton(modal) {
        const phase = Number(modal.getAttribute('data-rb-phase'));
        if (!this.isSelectable(phase)) return;

        const buyButtons = modal.querySelector('.buy-buttons');
        if (!buyButtons || buyButtons.querySelector('[data-rb-quickview-add]')) return;

        /* Marker-Klasse auf das MODAL statt display:none auf die
           einzelnen Buttons — aus zwei Gründen:
           1. Der Express-Checkout-Button (PayPal & Co.) liegt je nach
              Theme-Settings gar nicht in .buy-buttons, sondern eine
              Ebene höher (buy-buttons.liquid:330 vs. :300).
           2. Shopify rendert ihn asynchron nach; ein einmaliges
              querySelectorAll zum Injektionszeitpunkt würde ihn
              verpassen. Der CSS-Nachfahren-Selektor greift dagegen
              auch für später eingefügte Knoten.
           Klasse statt hidden-Attribut, weil die Theme-Regeln display
           setzen und [hidden] überstimmen würden (derselbe Fallstrick
           wie früher bei der Protokoll-Leiste). */
        modal.classList.add('mbr-rb-qv-replaced');

        /* Der Button wird aus dem Toggle der zugehörigen Karte GEKLONT
           statt neu gebaut. Damit trägt er dieselben Klassen und
           dasselbe Icon-Markup (Plus und Haken kommen aus Liquid, nicht
           als SVG-String aus dem JS) — und der ausgewählte Zustand
           sieht im Modal automatisch aus wie auf der Karte: Rahmen
           statt Vollton, Häkchen, "Selected". */
        const source = this.card(phase) && this.card(phase).querySelector('[data-rb-toggle]');
        if (!source) return;

        const button = source.cloneNode(true);
        button.removeAttribute('data-rb-toggle');
        button.setAttribute('data-rb-quickview-add', '');
        button.classList.add('mbr-rb__toggle--quickview');
        buyButtons.insertAdjacentElement('afterbegin', button);

        this.updateQuickViewButton(button, phase);

        this.on(button, 'click', () => {
          this.togglePhase(phase);
          modal.hide();
        });
      }

      /* Im Modal steht "Add to routine" statt "Add" (eigenes Setting),
         ausgewählt dasselbe Selected-Label wie auf der Karte. */
      updateQuickViewButton(button, phase) {
        const isOn = this.selected.has(phase);
        const label = isOn ? this.selectedLabel : this.quickViewAddLabel;

        button.setAttribute('aria-pressed', String(isOn));
        button.setAttribute('aria-label', label);

        const textEl = button.querySelector('.mbr-rb__toggle-text');
        if (textEl) textEl.textContent = label;
      }

      get isComplete() {
        return [1, 2, 3].every((phase) => this.selected.has(phase));
      }

      togglePhase(phase, source = 'card') {
        if (!this.isSelectable(phase)) return;

        const wasSelected = this.selected.has(phase);
        if (wasSelected) {
          this.selected.delete(phase);
        } else {
          this.selected.add(phase);
        }

        this.updateUI();

        /* Die Pill ist Navigation: sie fährt IHRE Karte an. Nur Karte
           und Add-Button springen nach dem Hinzufügen zur nächsten
           Phase weiter (und auch das nur beim Auswählen, nicht beim
           Abwählen). Vorher lief die Pill mit durch das Weiterspringen
           und landete dadurch immer eine Karte zu weit. */
        if (source === 'pill') {
          this.scrollToCard(phase);
        } else if (!wasSelected) {
          this.advanceToNextCard(phase);
        }

        this.dispatchEvent(
          new CustomEvent('routine:selection-changed', {
            bubbles: true,
            detail: {
              phase,
              selected: Array.from(this.selected).sort(),
              complete: this.isComplete
            }
          })
        );
      }

      updateUI() {
        [1, 2, 3].forEach((phase) => {
          const isOn = this.selected.has(phase);
          const card = this.card(phase);

          if (card) {
            card.classList.toggle('is-selected', isOn);
            const toggle = card.querySelector('[data-rb-toggle]');
            if (toggle) this.updateToggleLabel(toggle, card, isOn);
          }

          const pill = this.querySelector('[data-rb-pill][data-phase="' + phase + '"]');
          if (pill && !pill.disabled) pill.setAttribute('aria-pressed', String(isOn));
        });

        this.updateTotal();
        this.updateStatus();

        /* "Your protocol" erscheint erst, sobald mindestens ein Produkt
           gewählt wurde (Kundenfeedback: nicht mehr erst bei komplett).
           Klasse statt [hidden] — [hidden] erzwingt display:none, das
           lässt sich nicht animieren; die is-visible-Klasse steuert
           stattdessen den grid-template-rows-Aufklapp-Übergang in CSS.
           aria-hidden hält Screenreader während des Kollabierens fern. */
        if (this.summary) {
          const summaryVisible = this.selected.size > 0;
          this.summary.classList.toggle('is-visible', summaryVisible);
          this.summary.setAttribute('aria-hidden', String(!summaryVisible));
        }

        if (this.submitButton) this.submitButton.toggleAttribute('disabled', this.cartItems.length === 0);
      }

      get summary() {
        return this.querySelector('[data-rb-summary]');
      }

      get selectedLabel() {
        return this.getAttribute('data-selected-label') || 'Selected';
      }

      /* Button-Text/aria-label wechseln zwischen "Add" (aus Liquid
         gerendert, beim ersten Aufruf gecacht) und dem Selected-Label
         aus den Settings — statt eines zweiten data-Attributs pro Karte
         wird der ursprüngliche Text einmalig zwischengespeichert. */
      updateToggleLabel(toggle, card, isOn) {
        toggle.setAttribute('aria-pressed', String(isOn));

        const textEl = toggle.querySelector('.mbr-rb__toggle-text');
        if (!textEl) return;

        if (toggle.dataset.addLabel === undefined) toggle.dataset.addLabel = textEl.textContent;

        const label = isOn ? this.selectedLabel : toggle.dataset.addLabel;
        textEl.textContent = label;
        toggle.setAttribute('aria-label', label + ': ' + (card.getAttribute('data-product-title') || ''));
      }

      /* Nur kaufbare Positionen: markierte Phasen ohne Ausverkauftes. */
      get cartItems() {
        return Array.from(this.selected)
          .sort()
          .map((phase) => this.card(phase))
          .filter((card) => card && !card.hasAttribute('data-sold-out'))
          .map((card) => ({
            id: card.getAttribute('data-variant-id'),
            quantity: 1
          }));
      }

      updateTotal() {
        let subtotal = 0;
        this.selected.forEach((phase) => {
          const card = this.card(phase);
          if (card && !card.hasAttribute('data-sold-out')) {
            subtotal += parseInt(card.getAttribute('data-price'), 10) || 0;
          }
        });

        const element = this.querySelector('[data-rb-total]');
        if (element) element.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyWithCurrencyFormat);
      }

      updateStatus() {
        const statusEl = this.querySelector('[data-rb-status]');
        const noteEl = this.querySelector('[data-rb-note]');
        const complete = this.isComplete;

        if (noteEl) noteEl.toggleAttribute('hidden', !complete);
        if (!statusEl) return;

        if (complete) {
          statusEl.textContent = this.getAttribute('data-status-complete');
          return;
        }

        /* Die Ankerphase taucht nie in "fehlt noch" auf — der Satz meint
           die ANDEREN Phasen der Routine, unabhängig davon, ob der
           Anker selbst schon per Add-Klick hinzugefügt wurde. */
        const missing = [1, 2, 3]
          .filter((phase) => phase !== this.anchorPhase && !this.selected.has(phase))
          .map((phase) => this.phaseLabel(phase));
        const joiner = this.getAttribute('data-list-joiner') || 'and';
        const missingText = missing.join(' ' + joiner + ' ');

        statusEl.textContent = (this.getAttribute('data-status-incomplete') || '')
          .replace('[phase]', this.phaseLabel(this.anchorPhase))
          .replace('[missing]', missingText);
      }

      /* Struktureller Nachbau von CompactProductBundle.onSubmitHandler
         (assets/product-bundle.js) — gleiche Cart-, Pubsub- und
         Drawer-Integration, nur die items-Quelle ist unsere Auswahl. */
      onSubmitHandler(event) {
        const data = { items: this.cartItems };
        if (!data.items.length) return;

        if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
          theme.utils.postLink2(theme.routes.cart_add_url, {
            parameters: {
              ...data
            }
          });
          return;
        }

        event.preventDefault();
        if (this.submitButton.hasAttribute('aria-busy')) return;
        this.error = false;
        this.activeElement = event.submitter || event.currentTarget;

        this.handleErrorMessage();

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        const body = JSON.stringify({
          ...data,
          sections: sectionsToBundle,
          sections_url: window.location.pathname
        });

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.setAttribute('aria-busy', 'true');

        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.handleErrorMessage(parsedState.description);
              document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
                detail: {
                  errorMessage: parsedState.description
                }
              }));
              this.error = true;
              return;
            }

            const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
            cartJson['sections'] = parsedState['sections'];

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'mbr-routine-builder', cart: cartJson });
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
              detail: {
                product: parsedState
              }
            }));

            this.cartDrawer?.show(this.activeElement);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.submitButton.removeAttribute('aria-busy');

            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
        if (!this.errorMessage) return;

        this.errorMessage.toggleAttribute('hidden', !errorMessage);
        this.errorMessage.innerText = errorMessage || '';
      }
    }
  );
}
